/**
 * ═══════════════════════════════════════════════════════════════════════════════════════
 * TK-31 (lee 22/10) — NHÃN, MÀU và TÊN của đèn NEMA: THUẦN (không React, không API) để bộ kiểm nhập thẳng và ghim.
 *   `nemaApi.ts` xuất lại cho chỗ dùng cũ.
 *
 * Ngân sách màu: ĐỎ chỉ cho Không sáng / Mất điện (đèn hỏng thật) · vàng = cảnh báo · XÁM = mất kết nối / tắt / chưa rõ
 *   (chưa đo được không được kêu) · xanh lá = sáng · xanh dương = chưa quét. `kiem-tk31` [11] ghim bảng này.
 * ═══════════════════════════════════════════════════════════════════════════════════════
 */
export type BadgeDen = 'MAT_LIEN_LAC' | 'MAT_DIEN' | 'KHONG_SANG' | 'DANG_TAT' | 'LOI_DO' | 'CHUA_QUET' | 'SANG' | 'KHONG_TRA_LOI' | 'CHUA_RO'
  /** TK-35 (lee 22/10): ngõ ra cấp điện đang tắt ⇒ đèn không có nguồn — KHÔNG phải lỗi: xám nhạt, không đỏ. */
  | 'CHUA_CAP_DIEN';
export type ToneBadge = 'slate' | 'green' | 'red' | 'amber' | 'blue';

/** Nhãn + màu huy hiệu. */
export const NHAN_BADGE: Record<BadgeDen, { nhan: string; tone: ToneBadge }> = {
  SANG: { nhan: 'Sáng', tone: 'green' },
  DANG_TAT: { nhan: 'Tắt', tone: 'slate' },
  KHONG_SANG: { nhan: 'Không sáng', tone: 'red' },
  MAT_DIEN: { nhan: 'Mất điện', tone: 'red' },
  LOI_DO: { nhan: 'Lỗi đo', tone: 'amber' },
  KHONG_TRA_LOI: { nhan: 'Không trả lời', tone: 'amber' },
  MAT_LIEN_LAC: { nhan: 'Mất kết nối', tone: 'slate' },
  /* TK-48 §3.3: chưa có lần quét nào ra số sau mốc có điện — CHƯA BIẾT ⇒ XÁM (không xanh "đang chọn", không cam "khai") */
  CHUA_QUET: { nhan: 'Chưa quét', tone: 'slate' },
  CHUA_RO: { nhan: 'Chưa rõ', tone: 'slate' },
  /* TK-46 §2: CÙNG nghĩa với lý do `khong_co_dien` (ngõ của nhánh đèn đang tắt) ⇒ một chữ cho một tình trạng: "Ngõ ra tắt" */
  CHUA_CAP_DIEN: { nhan: 'Ngõ ra tắt', tone: 'slate' },
};

/**
 * Màu CHẤM đèn trên bản đồ — cùng ngân sách với huy hiệu. Mất kết nối dùng ĐÚNG token của tủ mất kết nối (`--chu-3`,
 * `BanDoTu`), tách khỏi xám nhạt của "Tắt" bằng độ đậm, không bằng một màu kêu (trước 22/10: tím `#8b5cf6`).
 */
export const MAU_BADGE: Record<BadgeDen, string> = {
  /* Vận hành 24/10: TOKEN của `thietke.css` (trước: mã Tailwind thô green-500 / amber-500 / slate-400 / red-500 / blue-400) —
     cùng màu với ô số, chấm tủ và huy hiệu; đổi chế độ sáng/tối tự theo. Chấm nằm trong `style` HTML nên `var()` chạy được. */
  SANG: 'rgb(var(--tot))', DANG_TAT: 'rgb(var(--chu-2))', KHONG_SANG: 'rgb(var(--nguy))', MAT_DIEN: 'rgb(var(--nguy))',
  LOI_DO: 'rgb(var(--canh))', KHONG_TRA_LOI: 'rgb(var(--canh))',
  MAT_LIEN_LAC: 'rgb(var(--chu-3))', CHUA_QUET: 'rgb(var(--chu-3))', CHUA_RO: 'rgb(var(--chu-3))',
  /* TK-35: xám NHẠT hơn "Tắt" — đèn không có nguồn, không có gì để kêu */
  CHUA_CAP_DIEN: '#cbd5e1',   // giữ mã: kiem-tk35 [4] ghim dạng hex cho trạng thái này (không xuất hiện trên dữ liệu thử)
};

/**
 * Tên đèn cho người đọc: tên đã đặt; chưa đặt ⇒ "Đèn <số thứ tự trong tủ>" (`stt` do MÁY CHỦ tính — `nemaTen.ts`).
 * ⛔ KHÔNG hiện địa chỉ hex (quy tắc 22/10). Vắng `stt` (máy chủ cũ) ⇒ "Đèn" — không bịa một số.
 */
export const tenDen = (d: { name: string | null; stt: number | null }) =>
  d.name?.trim() || (d.stt != null ? `Đèn ${d.stt}` : 'Đèn');
/** Đèn chưa có tên người đặt — khung chi tiết hiện nhãn nhỏ "chưa đặt tên". */
export const chuaDatTen = (d: { name: string | null }) => !d.name?.trim();

/**
 * TK-44 (lee 24/10) — VÌ SAO ĐÈN / TỦ KHÔNG NHẬN LỆNH. Máy chủ tính (`server/src/services/nemaDen.ts`, cùng thứ tự: tủ mất kết nối →
 * mesh tắt → ngõ ra tắt → đèn mất liên lạc) và trả `lyDo`; web chỉ đọc bảng chữ này (kiem-tk44 so khớp với `CHU_LY_DO` máy chủ).
 */
export type LyDoKhongNhan = 'mat_ket_noi_tu' | 'mesh_tat' | 'khong_co_dien' | 'mat_lien_lac';
export const CHU_LY_DO: Record<LyDoKhongNhan, string> = {
  mat_ket_noi_tu: 'Mất kết nối', mesh_tat: 'Mesh tắt', khong_co_dien: 'Ngõ ra tắt', mat_lien_lac: 'Đèn mất liên lạc',
};
export const chuLyDo = (l: LyDoKhongNhan | null | undefined): string | null => (l ? CHU_LY_DO[l] ?? null : null);
/** Đếm đèn nhận lệnh của một nhóm đèn (tủ / tuyến): x / y + lý do của đèn đầu tiên không nhận (để khoá nút khi x = 0). */
export function demNhanLenh(den: { nhanLenh?: boolean; lyDo?: LyDoKhongNhan | null }[]) {
  const nhan = den.filter((d) => d.nhanLenh === true).length;
  return { nhan, tong: den.length, lyDo: nhan > 0 ? null : (den.find((d) => d.lyDo)?.lyDo ?? null) };
}
/** Chữ khoá của MỘT đèn: Dim / Nháy cần đèn nhận lệnh; Trả về lịch chỉ cần tủ trực tuyến + mesh bật (SLCU giữ lịch). */
export function khoaLenhDen(d: { nhanLenh?: boolean; lyDo?: LyDoKhongNhan | null }) {
  return {
    dim: d.nhanLenh === true ? null : chuLyDo(d.lyDo) ?? 'Không nhận lệnh',
    traVe: d.lyDo === 'mat_ket_noi_tu' || d.lyDo === 'mesh_tat' ? chuLyDo(d.lyDo) : null,
  };
}

/** KA-49 §4.5: chuỗi nhãn đèn đang chọn "tên · mức % · P W" — NULL ⇒ "—" (luật số 0). Tên do người đặt ⇒ thoát HTML (divIcon). */
export function nhanDenChon(d: { dim: number | null; p_w: number | null }, ten: string): string {
  const thoat = (x: string) => x.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]!));
  return `${thoat(ten)} · ${d.dim == null ? '—' : d.dim} % · ${d.p_w == null ? '—' : d.p_w} W`;
}
